import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useDispatch, useSelector } from 'react-redux';
import { login, verifyOTP, resetAuthUiState } from '../../store/slices/authSlice';
import { getPostLoginPath } from '../../utils/postLoginRedirect';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import AuthLayout from './AuthLayout';

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { error } = useSelector((state) => state.auth);

  const [formData, setFormData] = useState({ username: '', password: '', remember: false });
  const [showPassword, setShowPassword] = useState(false);
  const [localError, setLocalError] = useState('');
  const [lockoutSeconds, setLockoutSeconds] = useState(0);
  const [showOtp, setShowOtp] = useState(false);
  const [otp, setOtp] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Session-expired toast
  useEffect(() => {
    if (location.state?.sessionExpired) {
      toast.warning('Your session has expired due to inactivity. Please log in again.', { toastId: 'session-expired' });
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Pre-fill from cookies & reset auth UI state
  useEffect(() => {
    dispatch(resetAuthUiState());
    const getCookie = (name) => {
      const match = document.cookie.split(';').map(c => c.trim()).find(c => c.startsWith(name + '='));
      return match ? decodeURIComponent(match.split('=')[1]) : null;
    };
    const rememberCookie = getCookie('remember');
    const usernameCookie = getCookie('username');
    if (rememberCookie === 'on' && usernameCookie) {
      setFormData(prev => ({ ...prev, username: usernameCookie, remember: true }));
    } else if (usernameCookie) {
      setFormData(prev => ({ ...prev, username: usernameCookie }));
    }
  }, [dispatch]);

  // Lockout countdown
  useEffect(() => {
    if (lockoutSeconds <= 0) return undefined;
    const timer = setInterval(() => {
      setLockoutSeconds(prev => (prev <= 1 ? 0 : prev - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, [lockoutSeconds]);

  // Clear error when username changes
  useEffect(() => {
    setLockoutSeconds(0);
    setLocalError('');
  }, [formData.username]);

  const formatLockoutTime = (s) => `${s >= 60 ? 59 : s}s`;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    if (localError && (name === 'username' || name === 'password') && value.length > 0 && lockoutSeconds <= 0) {
      setLocalError('');
    }
  };

  const handleOtpChange = (e) => {
    setOtp(e.target.value);
    if (localError && e.target.value.length > 0) setLocalError('');
  };

  const handleLogin = async () => {
    if (!formData.username && !formData.password) { setLocalError('Email/Username and password are required.'); return; }
    if (!formData.username) { setLocalError('Please enter your email or username.'); return; }
    if (!formData.password) { setLocalError('Please enter your password.'); return; }

    const resultAction = await dispatch(login({ username: formData.username, password: formData.password, remember: formData.remember }));
    if (login.fulfilled.match(resultAction)) {
      const data = resultAction.payload;
      if (data.requires_otp) { setShowOtp(true); setLocalError(''); }
      else { navigate(getPostLoginPath(data.user, data.redirectUrl), { replace: true }); }
    } else {
      const payload = resultAction.payload;
      if (payload && typeof payload === 'object' && payload.message) {
        setLocalError(payload.message);
        if (payload.retryAfterSeconds > 0) setLockoutSeconds(payload.retryAfterSeconds);
      } else {
        setLocalError(payload || 'Login failed');
      }
    }
  };

  const handleVerifyOtp = async () => {
    if (!otp) { setLocalError('Please enter the OTP.'); return; }
    const resultAction = await dispatch(verifyOTP({ otp }));
    if (verifyOTP.fulfilled.match(resultAction)) {
      const data = resultAction.payload;
      navigate(getPostLoginPath(data.user, data.redirectUrl), { replace: true });
    } else {
      setLocalError(resultAction.payload || 'OTP verification failed');
    }
  };

  const handleSubmit = async (e) => {
    if (e) { e.preventDefault(); e.stopPropagation(); }
    setLocalError('');
    if (isSubmitting || lockoutSeconds > 0) return;
    setIsSubmitting(true);
    try {
      if (showOtp) await handleVerifyOtp();
      else await handleLogin();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); e.stopPropagation(); handleSubmit(); }
  };

  return (
    <AuthLayout
      title={showOtp ? 'Two-Factor Verification' : 'Welcome to e-Vitals'}
      subtitle={
        showOtp
          ? 'Enter the 6-digit verification code sent to your email.'
          : 'Enter your credentials to access your remote patient monitoring portal.'
      }
    >
      <form
        onSubmit={handleSubmit}
        onKeyDown={handleKeyDown}
        className="lp-auth-form"
        noValidate
      >
        {!showOtp ? (
          <>
            <div className="lp-form-group">
              <label htmlFor="lp-email" className="lp-label">
                Email or username <span className="lp-required">*</span>
              </label>
              <div className="lp-input-container">
                <input
                  id="lp-email"
                  type="text"
                  name="username"
                  value={formData.username}
                  onChange={handleChange}
                  placeholder="Enter your email or username"
                  className="lp-input"
                  required
                  autoComplete="username"
                  autoFocus
                />
              </div>
            </div>

            <div className="lp-form-group">
              <label htmlFor="lp-password" className="lp-label">
                Password <span className="lp-required">*</span>
              </label>
              <div className="lp-input-container">
                <input
                  id="lp-password"
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="••••••••"
                  className="lp-input lp-password-input"
                  required
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className="lp-eye-btn"
                  onClick={() => setShowPassword(v => !v)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  tabIndex={-1}
                >
                  {showPassword ? <FaEyeSlash size={16} /> : <FaEye size={16} />}
                </button>
              </div>
            </div>

            <div className="lp-extra-row">
              <label className="lp-remember-wrap">
                <input
                  type="checkbox"
                  name="remember"
                  checked={formData.remember}
                  onChange={handleChange}
                  className="lp-checkbox"
                />
                <span>Remember me</span>
              </label>
              <a
                href="/forgot-password"
                className="lp-forgot-pass-link"
                onClick={(e) => { e.preventDefault(); navigate('/forgot-password'); }}
              >
                Forgot Password?
              </a>
            </div>
          </>
        ) : (
          <div className="lp-form-group">
            <label htmlFor="lp-otp" className="lp-label">
              Enter OTP <span className="lp-required">*</span>
            </label>
            <div className="lp-input-container">
              <input
                id="lp-otp"
                type="text"
                value={otp}
                onChange={handleOtpChange}
                placeholder="Enter 6-digit OTP"
                className="lp-input"
                required
                autoFocus
              />
            </div>
          </div>
        )}

        {(localError || lockoutSeconds > 0) && (
          <p className="lp-form-error">
            {lockoutSeconds > 0
              ? `Too many failed attempts. Please try again in ${formatLockoutTime(lockoutSeconds)}.`
              : localError}
          </p>
        )}

        <button
          type="submit"
          className="lp-btn-signin"
          id="login-btn"
          disabled={isSubmitting || lockoutSeconds > 0}
        >
          {isSubmitting && <span className="lp-btn-spinner" />}
          <span>
            {isSubmitting
              ? (showOtp ? 'Verifying...' : 'Signing In...')
              : (showOtp ? 'Verify OTP' : 'Sign In')}
          </span>
        </button>

        {showOtp && (
          <div className="lp-back-wrap">
            <button
              type="button"
              className="lp-forgot-pass-link"
              onClick={() => { setShowOtp(false); setOtp(''); setLocalError(''); }}
            >
              ← Back to Sign In
            </button>
          </div>
        )}
      </form>
    </AuthLayout>
  );
};

export default LoginPage;
